import { useCallback, useEffect, useState } from 'react';

import CustomButton from '@perpay-web/components/base/CustomButton/CustomButton';
import Pill from '@perpay-web/components/base/Pill/Pill';
import FocusContent from '@perpay-web/components/composite/FocusLayout/FocusContent';
import FocusFooter from '@perpay-web/components/composite/FocusLayout/FocusFooter';
import FocusLayout from '@perpay-web/components/composite/FocusLayout/FocusLayout';
import Stack from '@perpay-web/components/design-system/Stack/Stack';
import CardCta from '@perpay-web/fintech/components/base/CardCta/CardCta';
import CardApplicationHeader from '@perpay-web/fintech/components/base/Header/CardApplicationHeader';
import PerpaySplitInfo from '@perpay-web/fintech/components/base/PerpaySplitInfo/PerpaySplitInfo';
import Stepper from '@perpay-web/fintech/components/base/Stepper/Stepper';
import { ChooseBankAccountButton } from '@perpay-web/fintech/components/composite/ChooseBankAccountButton/ChooseBankAccountButton';
import {
    cardApplicationStepperNames,
    REVIEW_DIRECT_DEPOSIT_STEP,
} from '@perpay-web/fintech/components/screens/CardApplication/CardApplicationSteps';
import { useFullHeader } from '@perpay-web/fintech/hooks/useFullHeaderContext';
import { setPrimaryBankAccount } from '@perpay-web/fintech/utils/bankAccountUtils';
import { getClassName } from '@perpay-web/utils/domUtils';
import { noop } from '@perpay-web/utils/noop';
import { formatCurrency } from '@perpay-web/utils/stringUtils';

import styles from './SSICardApplicationDirectDepositSummary.scss';

const SSICardApplicationDirectDepositSummary = ({
    partnerCode,
    onClickContinue = noop,
    onUnmount = noop,
    onMount = noop,
    totalMinimumPayment,
}) => {
    const renderFullHeaderCb = useCallback(
        () => <CardApplicationHeader partnerCode={partnerCode} />,
        [partnerCode],
    );

    useEffect(() => {
        onMount();
        return () => {
            onUnmount();
        };
    }, [onMount, onUnmount]);

    const [bankAccountUUID, setBankAccountUUID] = useState(null);

    const handleChangeBankAccount = (bankAccount) => {
        setBankAccountUUID(bankAccount.uuid);
        setPrimaryBankAccount(bankAccount.uuid);
    };

    const renderStepper = () => {
        const currentStep = cardApplicationStepperNames.findIndex(
            (step) => step.step === REVIEW_DIRECT_DEPOSIT_STEP,
        );

        return (
            <Stepper
                steps={cardApplicationStepperNames}
                currentStep={currentStep}
                className={getClassName(styles.stepper)}
            />
        );
    };

    useFullHeader(renderFullHeaderCb);
    return (
        <FocusLayout className={styles.container}>
            {renderStepper()}
            <FocusContent className={styles.content}>
                <Stack className={styles['inner-stack-tight']}>
                    <div className={styles.header}>
                        Direct deposit & your card
                    </div>
                    <div className={styles.copy}>
                        Perpay is designed for you to send just a portion of
                        your paycheck. Unfortunately, your benefits do not allow
                        deposits to be split to multiple accounts. In order to
                        proceed, you&apos;ll need to direct your full benefit
                        amount to your Perpay Account.
                    </div>
                </Stack>
                <PerpaySplitInfo amount={formatCurrency(totalMinimumPayment)} />
                <div className={styles.details}>
                    <div className={styles.header2}>
                        Next, add your bank account
                    </div>
                    <Pill className={styles.pill}>Required</Pill>
                </div>
                <ChooseBankAccountButton
                    onChange={handleChangeBankAccount}
                    skipMFA
                />
                <div className={styles.footer__disclaimer}>
                    *Funds are sent to your bank account via ACH through
                    JPMorgan Chase. Actual availability may vary depending on
                    your bank’s processing times and policies.
                </div>
                <div className={styles['footer__direct-deposit-disclaimer']}>
                    Direct deposit is required in order to access the Perpay
                    Credit Card. You will have control of funds that are sent to
                    Perpay.{' '}
                    <strong>
                        Your offer will expire if your direct deposit is not
                        received within 60 days.
                    </strong>
                </div>
            </FocusContent>
            <FocusFooter className={styles['footer-container']}>
                <CardCta
                    className={styles.footer__button}
                    onClick={onClickContinue}
                    Component={CustomButton}
                    disabled={!bankAccountUUID}
                >
                    Continue
                </CardCta>
            </FocusFooter>
        </FocusLayout>
    );
};

export default SSICardApplicationDirectDepositSummary;
